import { notify } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { APIError } from 'src/utils'

import { usePollAttachmentZipDownload } from '@features/attachments/hooks/usePollAttachmentZipDownload'
import { createAttachmentZipDownload, CreateAttachmentZipDownloadProps } from '@features/attachments/query-api'

import { NotificationKeys } from '../../../enums/notificationKeys'

export const useDownloadReceiptsZip = () => {
  const { t } = useTranslation()
  const [attachmentZipDownloadId, setAttachmentZipDownloadId] = useState<string | null>(null)

  const notifyError = useCallback(
    (error?: APIError) => {
      notify({
        id: NotificationKeys.ReceiptsList,
        message: error?.body?.errorMessage || t('receipts.download_zip.error'),
        variant: 'error',
      })
    },
    [t],
  )

  const createZipMutation = useMutation(createAttachmentZipDownload, {
    onSuccess: (data) => {
      setAttachmentZipDownloadId(data.attachmentZipDownloads[0]?.id ?? null)
    },
    onError: (error: APIError) => {
      notifyError(error)
    },
  })

  usePollAttachmentZipDownload({
    polling: !!attachmentZipDownloadId,
    attachmentZipDownloadId,
    onSuccess: (attachmentZipDownload) => {
      setAttachmentZipDownloadId(null)

      if (!attachmentZipDownload.downloadUrl) {
        return
      }

      // A hidden <a> click triggers a file download rather than opening a new browsing
      // context, so — unlike window.open — it isn't subject to the browser's popup
      // blocker and works no matter how long the zip took to prepare.
      const downloadLink = document.createElement('a')
      downloadLink.href = attachmentZipDownload.downloadUrl
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    },
    onError: () => {
      setAttachmentZipDownloadId(null)
      notifyError()
    },
  })

  const downloadReceiptsZip = useCallback(
    (payload: CreateAttachmentZipDownloadProps) => {
      createZipMutation.mutate(payload)
    },
    [createZipMutation],
  )

  return {
    isDownloading: createZipMutation.isLoading || !!attachmentZipDownloadId,
    downloadReceiptsZip,
  }
}
